import { Badge, Table, TableContainer, Tbody, Td, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterDateRange, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { FormModal } from '../../../components/FormModal'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast } from '../../../helpers'
import IFilter, { IPersonnelMovementRequest } from '../../../services/PersonnelMovementRequests/interfaces'
import PersonnelMovementRequestsService from '../../../services/PersonnelMovementRequests/PersonnelMovementRequestsService'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { UserService } from '../../../services/User/UserService'
import PersonnelMovementRequestViewPage from './PersonnelMovementRequestViewPage'

const INITIAL_FILTER: IFilter = {
  date: { start: '', end: '' },
  type: '',
  name: '',
  sectorId: 0
}

const TYPE_OPTIONS = [
  'Aumentar a Equipe',
  'Reduzir a Equipe',
  'Reconhecer alguém da equipe',
  'Substituir alguém da equipe',
  'Transferir alguém da equipe'
].map((type) => ({ value: type, label: type }))

export default function PersonnelMovementRequestsPage() {
  const modalState = useDisclosure()
  const [personnelMovementRequestId, setPersonnelMovementRequestId] = useState<number>()
  const hasRole = useHasRole()
  const toast = useAppToast()
  const personnelMovementRequestsService = useMemo(() => new PersonnelMovementRequestsService(), [])
  const [personnelMovementRequests, setPersonnelMovementRequests] = useState<IPersonnelMovementRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const sectorService = useMemo(() => new SectorService(), [])
  const [sectors, setSectors] = useState<ISector[]>([])
  const userService = new UserService()
  const [userSectorsIds, setUserSectorsIds] = useState<number[]>([])
  const permissions = {
    addPersonnelMovementRequests: hasRole('add_personnel_movement_requests'),
    deletePersonnelMovementRequests: hasRole('delete_personnel_movement_requests'),
    downloadPersonnelMovementRequests: hasRole('download_personnel_movement_requests'),
    downloadSectorPersonnelMovementRequests: hasRole('download_sector_personnel_movement_requests')
  }
  const [filter, setFilter] = useState<IFilter>(INITIAL_FILTER)
  const isDirty =
    filter.name !== '' ||
    filter.type !== '' ||
    filter.sectorId !== 0 ||
    filter.date.start !== '' ||
    filter.date.end !== ''
  const [deletingId, setDeletingId] = useState(0)

  const fetchPersonnelMovementRequests = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await personnelMovementRequestsService.list(filter)
      setPersonnelMovementRequests(response.data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as solicitações', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSectors = async () => {
    try {
      const response = await sectorService.listForSelect()
      setSectors(response)
    } catch {
      toast.error('Erro ao buscar a lista de Setores!')
    }
  }

  const fetchUserSectors = () => {
    userService.getSectors().then((response) => {
      setUserSectorsIds(response.map((sectors) => sectors.id))
    })
  }

  const handleDelete = async () => {
    try {
      await personnelMovementRequestsService.delete(deletingId)
      fetchPersonnelMovementRequests()

      toast.success('Solicitação deletada com sucesso!')
    } catch {
      toast.error('Erro ao deletar solicitação!')
    } finally {
      setDeletingId(0)
    }
  }

  useEffect(() => {
    fetchSectors()
    fetchUserSectors()
  }, [])

  // Filtra ao vivo, como as demais listagens — o botão "Filtrar" existia porque um campo de
  // texto sem debounce dispararia uma requisição por tecla, o que o `FilterSearch` resolve.
  useEffect(() => {
    fetchPersonnelMovementRequests()
  }, [filter])

  return (
    <>
      <PageTitle title="Solicitações de MP" description="Movimentações de pessoal solicitadas pelos gestores" />

      {permissions.addPersonnelMovementRequests && (
        <ActionFab to="/personnel-movement/new" label="Nova movimentação de pessoal" openInNewTab />
      )}

      <FilterBar onClear={() => setFilter(INITIAL_FILTER)} isDirty={isDirty}>
        <FilterSearch
          label="Nome"
          value={filter.name}
          onChange={(name) => setFilter({ ...filter, name })}
          placeholder="Buscar por nome"
        />
        <FilterSelect
          label="Setor"
          value={filter.sectorId}
          onChange={(sectorId) => setFilter({ ...filter, sectorId: Number(sectorId) })}
          options={sectors.map((sector) => ({ value: sector.id, label: sector.name }))}
          // A listagem trata `0` como "sem filtro de setor", não `''`.
          emptyValue={0}
        />
        <FilterSelect
          label="Tipo"
          value={filter.type}
          onChange={(type) => setFilter({ ...filter, type })}
          options={TYPE_OPTIONS}
        />
        <FilterDateRange label="Data" value={filter.date} onChange={(date) => setFilter({ ...filter, date })} />
      </FilterBar>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Nome</Th>
              <Th>Setor</Th>
              <Th>Tipo</Th>
              <Th>Descritivo Ok</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="solicitações" feminine onRetry={fetchPersonnelMovementRequests} />
            ) : personnelMovementRequests.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="solicitação" feminine />
            ) : (
              personnelMovementRequests.map((personnelMovementRequest) => (
                <Tr key={personnelMovementRequest.id}>
                  <Td>{personnelMovementRequest.id}</Td>
                  <Td>{personnelMovementRequest.name}</Td>
                  <Td>{sectors.find((sector) => sector.id === personnelMovementRequest.sector)?.name}</Td>
                  <Td>{personnelMovementRequest.type}</Td>
                  <Td>
                    {personnelMovementRequest.descriptive_ok === null ? (
                      <EmptyCell />
                    ) : (
                      <Badge colorScheme={personnelMovementRequest.descriptive_ok ? 'green' : 'orange'}>
                        {personnelMovementRequest.descriptive_ok ? 'Sim' : 'Não'}
                      </Badge>
                    )}
                  </Td>
                  <Td>{formatDateTime(personnelMovementRequest.created_at)}</Td>
                  <Td>
                    {personnelMovementRequest.step == 0 && (
                      <>
                        <ActionButton
                          action="view"
                          onClick={() => {
                            modalState.onOpen()
                            setPersonnelMovementRequestId(personnelMovementRequest.id)
                          }}
                        />
                        {(permissions.downloadPersonnelMovementRequests ||
                          (userSectorsIds.includes(personnelMovementRequest.sector) &&
                            permissions.downloadSectorPersonnelMovementRequests)) && (
                          <a
                            href={personnelMovementRequest.link_pdf}
                            download="Solicitação de Movimentação de Pessoal.pdf"
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ActionButton action="download" />
                          </a>
                        )}
                      </>
                    )}
                    {personnelMovementRequest.step != 0 && permissions.addPersonnelMovementRequests && (
                      <ActionButton action="edit" to={`/personnel-movement/${personnelMovementRequest.id}`} />
                    )}
                    {permissions.deletePersonnelMovementRequests && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setDeletingId(personnelMovementRequest.id)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <FormModal
        isOpen={modalState.isOpen}
        onClose={modalState.onClose}
        size="wide"
        title="Solicitação de Movimentação de Pessoal"
      >
        {personnelMovementRequestId !== undefined && (
          <PersonnelMovementRequestViewPage personnelMovementRequestId={personnelMovementRequestId} sectors={sectors} />
        )}
      </FormModal>

      <ConfirmDialog
        isOpen={deletingId > 0}
        onClose={() => setDeletingId(0)}
        onConfirm={handleDelete}
        action="delete"
        entity="solicitação de MP"
        feminine
      />
    </>
  )
}
